import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  LinearProgress,
  TextField,
  Theme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import Field from "components/Field";
import { pick } from "lodash";
import { DropzoneAreaBase, FileObject } from "mui-file-dropzone";
import { useMemo, useState } from "react";
import { FORM_ERROR } from "final-form";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  FileUploadRequest,
  IMediaItem,
  MediaItemSource,
  ResponseCode,
} from "services/KnowfullyAdminClient";
import { MediaFilterType, getContentTypesForFilters, allMediaLibraryTypes } from "utils/mediaUtils";
import { DeliveryMethodHelper } from "utils/deliveryMethodHelper";
import translateError from "utils/translateError";
import MediaLibraryItemPreview from "./MediaLibraryItemPreview";

import { BlockBlobClient } from "@azure/storage-blob";
import useGlobalContext from "hooks/globalData";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "20px",
    width: "100%",
    position: "relative",
  },
  hidden: {
    display: "none",
  },
  progress: {
    width: "100%",
    marginTop: "20px",
  },
});

const getMaxFileSizeInBytes = (source: MediaItemSource) => {
  switch (source) {
    case MediaItemSource.CourseVideo:
    case MediaItemSource.ArticulateRise:
    case MediaItemSource.InfographEd:
    case MediaItemSource.ISpring:
      return 1024 * 1024 * 1024; // 1GB for Video and Zip
    case MediaItemSource.CoursePdf:
      return 100 * 1024 * 1024; // 100MB for PDF
    default:
      return 50 * 1024 * 1024; // 50MB for others
  }
};

export type MediaLibraryItemFormValues = Partial<IMediaItem>;

const { Form } = withTypes<MediaLibraryItemFormValues>();

type MediaLibraryItemFormProps = {
  source: MediaItemSource;
  options?: MediaFilterType[];
  mediaLibraryItem?: IMediaItem;
  alternativeTextRequired?: boolean;
  customAlternativeTextLabel?: string;
  organizationPickerMode?: ContextOrganizationFieldMode;
  organizationIdOverride?: string;
  maxFileSizeBytes?: number;
  onSubmit: (
    values: MediaLibraryItemFormValues,
    file: File | null
  ) => object | Promise<object | void> | void;
  onCancel?: () => void;
};

const MediaLibraryItemForm = ({
  source,
  options = allMediaLibraryTypes,
  mediaLibraryItem,
  organizationPickerMode,
  alternativeTextRequired = true,
  customAlternativeTextLabel,
  submitLabel,
  organizationIdOverride,
  maxFileSizeBytes,
  onCancel,
  onSubmit,
}: MediaLibraryItemFormProps &
  Omit<FormProps<MediaLibraryItemFormValues>, "onSubmit">) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { mediaService } = useGlobalContext();

  const [error, setError] = useState<string | null>();
  const [fileObject, setFileObject] = useState<FileObject | null>(null);
  const [fileUploadUrl, setFileUploadUrl] = useState<string>();
  const [uploadedBlobName, setUploadedBlobName] = useState<string>();
  const [dimensions, setDimensions] = useState<string>();
  const [isUploading, setIsUploading] = useState(false);
  const fileSizeBytesLimit = maxFileSizeBytes ?? getMaxFileSizeInBytes(source);

  const init = useMemo<MediaLibraryItemFormValues>(
    () => ({
      ...pick(mediaLibraryItem, [
        "organizationId",
        "alternativeText",
        "description",
      ]),
      ...(!!organizationIdOverride
        ? { organizationId: organizationIdOverride }
        : {}),
    }),
    [mediaLibraryItem, organizationIdOverride]
  );

  const isEdit = !!mediaLibraryItem;
  const displayDropzone = !fileObject && !isEdit;
  const displayForm = fileObject || isEdit;

  const handleFileUpload = async (fileObject: FileObject) => {
    if (!fileObject) return;

    setIsUploading(true);

    try {
      const fileUploadUrlResponse = await mediaService.generateFileUploadUrl(
        FileUploadRequest.fromJS({
          source: source,
          fileName: fileObject.file.name,
        })
      );

      if (fileUploadUrlResponse.responseCode !== ResponseCode.OK) {
        throw new Error(fileUploadUrlResponse.responseReason);
      }

      const blockBlobClient = new BlockBlobClient(fileUploadUrlResponse.data!.uploadUrl!);

      await blockBlobClient.uploadData(fileObject.file, {
        blobHTTPHeaders: { blobContentType: fileObject.file.type },
        metadata: {
          originalfilename: encodeURIComponent(fileObject.file.name),
        },
      });

      const fileUploaded = await blockBlobClient.exists();
      if (!fileUploaded) {
        throw new Error(t({ id: "fileNotFound" }));
      }

      setFileUploadUrl(fileUploadUrlResponse.data!.assetUrl!);
      setUploadedBlobName(fileUploadUrlResponse.data!.blobName!);
      setFileObject(fileObject);
    } catch (uploadError) {
      setError(t({ id: "uploadingFileError" }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (values: MediaLibraryItemFormValues) => {
    try {
      const transformed = {
        ...values,
        dimensions,
        ...(isEdit
          ? {
              id: mediaLibraryItem.id,
              fileName: mediaLibraryItem.fileName,
              fileSize: mediaLibraryItem.fileSize,
              contentType: mediaLibraryItem.contentType,
              fileUrl: mediaLibraryItem.fileUrl,
            }
          : {
              fileName: fileObject?.file?.name,
              fileSize: fileObject?.file?.size,
              contentType: fileObject?.file?.type,
              initialFileUrl: fileUploadUrl!,
              blobName: uploadedBlobName!,
              fileUrl: await DeliveryMethodHelper.GetDeliveryMethodAccessUrl(fileUploadUrl!, fileObject?.file!, source),
            }),
      };

      return await onSubmit(transformed, fileObject?.file ?? null);
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <>
      {fileObject && !isEdit && (
        <MediaLibraryItemPreview
          fileName={fileObject.file.name}
          fileSize={fileObject.file.size}
          contentType={fileObject.file.type}
          dimensions={dimensions}
          imageSrc={fileObject.data as string}
          onLoad={(e: any) => {
            const target = e.target as HTMLImageElement;
            setDimensions(
              `${target.naturalWidth} x ${target.naturalHeight} px`
            );
          }}
        />
      )}
      {isEdit && mediaLibraryItem && (
        <MediaLibraryItemPreview
          fileName={mediaLibraryItem.fileName!}
          fileSize={mediaLibraryItem.fileSize!}
          contentType={mediaLibraryItem.contentType!}
          dimensions={mediaLibraryItem.dimensions}
          imageSrc={mediaLibraryItem.fileUrl}
          altText={mediaLibraryItem.alternativeText}
          uploadedOn={mediaLibraryItem.createdOn}
          uploadedBy={mediaLibraryItem.createdBy}
          onLoad={(e: any) => {
            const target = e.target as HTMLImageElement;
            setDimensions(
              `${target.naturalWidth} x ${target.naturalHeight} px`
            );
          }}
        />
      )}
      <Box sx={displayDropzone ? styles.dropzone : styles.hidden}>
        <DropzoneAreaBase
          acceptedFiles={getContentTypesForFilters(options)}
          maxFileSize={fileSizeBytesLimit}
          filesLimit={1}
          onAdd={(file: FileObject[]) => {
            setDimensions(undefined);
            handleFileUpload(file[0]);
          }}
          fileObjects={[]}
          showAlerts={["error", "info"]}
          showPreviews={true}
          showFileNamesInPreview={true}
          dropzoneProps={{
            disabled: isUploading,
          }}
        />
        {isUploading && <LinearProgress sx={styles.progress} />}
      </Box>

      <Form
        onSubmit={handleSubmit}
        initialValues={init}
        render={({
          form: { change },
          handleSubmit,
          submitting,
          submitError,
          values,
        }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              {displayForm && (
                <>
                  <DialogContent>
                    <Grid container spacing={2}>
                      <ContextOrganizationField
                        values={values}
                        formChange={change}
                        mode={organizationPickerMode}
                      />
                      <Field
                        name="alternativeText"
                        required={alternativeTextRequired}
                      >
                        {({ input, meta }) => (
                          <Grid item xs={12}>
                            <TextField
                              variant="standard"
                              fullWidth
                              required={alternativeTextRequired}
                              InputLabelProps={{
                                required: alternativeTextRequired,
                              }}
                              label={t({
                                id:
                                  customAlternativeTextLabel ??
                                  "alternativeTextLabel",
                              })}
                              error={meta.error && meta.submitFailed}
                              helperText={
                                meta.error && meta.submitFailed && meta.error
                              }
                              {...input}
                            />
                          </Grid>
                        )}
                      </Field>

                      <Field name="description">
                        {({ input, meta }) => (
                          <Grid item xs={12}>
                            <TextField
                              variant="standard"
                              fullWidth
                              label={t({ id: "descriptionLabel" })}
                              error={meta.error && meta.submitFailed}
                              helperText={
                                meta.error && meta.submitFailed && meta.error
                              }
                              {...input}
                            />
                          </Grid>
                        )}
                      </Field>

                      {(error || submitError) && (
                        <Alert sx={styles.feedback} severity="error">
                          {error ?? submitError}
                        </Alert>
                      )}
                    </Grid>
                  </DialogContent>

                  <DialogActions>
                    <Button disabled={submitting} onClick={onCancel}>
                      {t({ id: "cancelButton" })}
                    </Button>

                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      disabled={submitting || isUploading}
                    >
                      {submitLabel}
                    </Button>
                  </DialogActions>
                </>
              )}
            </form>
          );
        }}
      />
    </>
  );
};

export default MediaLibraryItemForm;
